import { ButtonV2, Dialog, TextArea } from '@cimplico/components';
import { useState } from 'react';
import { toastNotification } from '../../../../components/toast/Toast';
import { useCancelSubscription } from '../../../../queries/billing/use-cancel-subscription';
import { SubscriptionV2 } from '../../../../types/firm';
import { formatDate } from '../../../../utilities/formatting';

type CancelSectionProps = {
    subscription: SubscriptionV2;
};

function CancelSection({ subscription }: CancelSectionProps): JSX.Element {
    const [confirm, setConfirm] = useState(false);
    const [form, setForm] = useState<{ feedback: string; comment?: string }>({ feedback: 'other', comment: undefined });

    const query = useCancelSubscription();

    function cancel(): void {
        query.mutate(form, {
            onSuccess: () => {
                setConfirm(false);
                toastNotification('success', 'Subscription cancelled successfully');
            },

            onError: () => {
                toastNotification('error', 'Error cancelling subscription');
            },
        });
    }

    return (
        <>
            {confirm && (
                <Dialog
                    type="error"
                    title="Are you sure you want to cancel your subscription?"
                    description={`Your subscription is scheduled to end on ${formatDate(
                        subscription.currentPeriodEnd
                    )}. You'll keep full access until then.
This action cannot be undone.`}
                    isOpen
                    primaryAction="Cancel Subscription"
                    secondaryAction="Close"
                    onClose={(): void => setConfirm(false)}
                    onSecondaryActionClick={(): void => setConfirm(false)}
                    onPrimaryActionClick={(): void => cancel()}
                    isLoading={query.isLoading}
                >
                    <div className="pr-16 pb-4">
                        <div className="mb-4">
                            <label className="block text-sm font-medium text-gray-700 mb-1">Reason for cancellation</label>
                            <select
                                className="w-full p-2 border border-borderPrimary rounded text-contentSecondary text-sm ring-indigo-600 focus:ring-2"
                                onChange={(e): void => {
                                    setForm({ ...form, feedback: e.target.value });
                                }}
                                value={form.feedback}
                            >
                                <option value="customer_service">Customer Service</option>
                                <option value="low_quality">Low Quality</option>
                                <option value="missing_features">Missing Features</option>
                                <option value="other">Other</option>
                                <option value="switched_service">Switched Service</option>
                                <option value="too_complex">Too Complex</option>
                                <option value="too_expensive">Too Expensive</option>
                                <option value="unused">Unused</option>
                            </select>
                        </div>

                        <TextArea
                            placeholder="Optionally provide any additional information..."
                            characterLimit={200}
                            isOptional
                            title="Comments"
                            onChange={(v): void => {
                                setForm({ ...form, comment: v });
                            }}
                            defaultValue={form.comment}
                        />
                    </div>
                </Dialog>
            )}
            <div>
                <div className="flex gap-16 flex-wrap">
                    <div className="w-[400px]">
                        <h3 className="text-contentPrimary font-bold">Cancellation</h3>
                        <p className="text-sm text-contentSecondary mt-1">Cancel your subscription to Cimplico Workpapers.</p>
                    </div>
                    <div className="flex-1">
                        <ButtonV2
                            type="danger"
                            label="Cancel Subscription"
                            size="sm"
                            onClick={(): void => setConfirm(true)}
                            isDisabled={Boolean(subscription.cancelAt) || Boolean(subscription.cancelledOn)}
                        />
                    </div>
                </div>
            </div>
        </>
    );
}

export default CancelSection;
